/**
 * Donors (GH-857, DEC-049): the console's Donors section, and the donor's own switch.
 *
 *   GET  /v1/organizations/:organizationId/donors                 donor.view   list, server-paged
 *   GET  /v1/organizations/:organizationId/donors/summary         donor.view   totals, frequency
 *   GET  /v1/organizations/:organizationId/donors/:donorRef       donor.view   one donor
 *   GET  /v1/organizations/:organizationId/donors.csv             donor.export (step-up; audited)
 *   POST /v1/organizations/:organizationId/donor-messages/preview donor.message
 *   POST /v1/organizations/:organizationId/donor-messages         donor.message (rate limited)
 *   GET|POST /v1/public/email/unsubscribe?t=…                     the signed one-click link
 *   GET  /v1/donor/organization-visibility                        the signed-in donor
 *   PUT  /v1/donor/organization-visibility                        the signed-in donor, confirmed
 *
 * ## What "anonymous" means (owner, 2026-10-03)
 *
 * Anonymous hides a donor's name from the public — other donors, live displays, the recognition
 * feed. It never hides them from the organization they gave to: staff see the name with an
 * "Anonymous publicly" badge (`publiclyAnonymous`). A donor who also wants to be anonymous to
 * organizations sets "Anonymous to all organizations" on their profile; they then appear nowhere
 * in these responses except inside an aggregate.
 *
 * Money is integer minor units as strings, like every other money field.
 */
import { z } from "zod";
import { moneySchema } from "./v1.js";
export const DONOR_FREQUENCIES = ["regular", "occasional", "one_time"];
export const DONOR_SORTS = ["total_desc", "recent", "name", "gifts_desc", "first_gift"];
/** Which gifts count: all of them, the last 12 months, or the last 30 days. */
export const DONOR_WINDOWS = ["all", "12m", "30d"];
export const DONOR_PAGE_SIZES = [25, 50, 100];
/** The 32-hex, organization-scoped reference for a donor. Never a profile id. */
export const donorRefSchema = z.string().regex(/^[0-9a-f]{32}$/, "not a donor reference");
/** Filters shared by the list, the summary, the export and a message's segment. */
export const donorFiltersSchema = z
    .object({
    q: z.string().trim().max(100).optional(),
    eventId: z.string().uuid().optional(),
    designationId: z.string().uuid().optional(),
    window: z.enum(DONOR_WINDOWS).default("all"),
    frequency: z.enum(DONOR_FREQUENCIES).optional(),
})
    .strict();
export const donorListQuerySchema = donorFiltersSchema
    .extend({
    sort: z.enum(DONOR_SORTS).default("total_desc"),
    page: z.coerce.number().int().min(1).max(10_000).default(1),
    pageSize: z.coerce
        .number()
        .int()
        .refine((value) => DONOR_PAGE_SIZES.includes(value), "25, 50 or 100")
        .default(25),
})
    .strict();
export const donorPostalAddressSchema = z
    .object({
    line1: z.string(),
    line2: z.string().nullable().optional(),
    city: z.string(),
    region: z.string(),
    postalCode: z.string(),
})
    .passthrough();
export const donorRowSchema = z.object({
    donorRef: donorRefSchema,
    /** Null when the donor left an address but no name. */
    name: z.string().nullable(),
    /** Null when the donor left none — or when the caller's role does not see contact details. */
    email: z.string().nullable(),
    phone: z.string().nullable(),
    postalAddress: donorPostalAddressSchema.nullable(),
    total: moneySchema,
    giftCount: z.number().int().nonnegative(),
    firstGiftAt: z.string(),
    lastGiftAt: z.string(),
    frequency: z.enum(DONOR_FREQUENCIES),
    /** Distinct months with a gift in the last six. */
    recentMonths: z.number().int().nonnegative(),
    /** The donor chose public anonymity on at least one gift: do not name them publicly. */
    publiclyAnonymous: z.boolean(),
    eventsAttended: z.number().int().nonnegative(),
    ticketsBought: z.number().int().nonnegative(),
});
export const donorListResponseSchema = z.object({
    donors: z.array(donorRowSchema),
    total: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    /** What this caller may do, answered by the server so the console never guesses. */
    capabilities: z.object({
        contact: z.boolean(),
        export: z.boolean(),
        message: z.boolean(),
    }),
});
const aggregateSchema = z.object({ giftCount: z.number().int().nonnegative(), total: moneySchema });
export const donorSummaryResponseSchema = z.object({
    donorCount: z.number().int().nonnegative(),
    total: moneySchema,
    giftCount: z.number().int().nonnegative(),
    frequency: z.object({
        regular: z.number().int().nonnegative(),
        occasional: z.number().int().nonnegative(),
        oneTime: z.number().int().nonnegative(),
    }),
    publiclyAnonymous: z.number().int().nonnegative(),
    /**
     * Gifts from donors anonymous to all organizations: counted, never named. Null below five gifts,
     * with `hiddenWithheld` true, so a small bucket cannot point at a person.
     */
    hidden: aggregateSchema.nullable(),
    hiddenWithheld: z.boolean(),
    /** Gifts that left neither a name nor an address. */
    unidentified: aggregateSchema,
});
export const donorDetailResponseSchema = z.object({
    donor: donorRowSchema,
    gifts: z.array(z.object({
        giftId: z.string().uuid(),
        givenAt: z.string(),
        amount: moneySchema,
        eventName: z.string().nullable(),
        designationName: z.string().nullable(),
        publiclyAnonymous: z.boolean(),
        receiptNumber: z.string().nullable(),
        /** The note the donor wrote with the gift, for the organization. */
        donorNote: z.string().nullable(),
    })),
    /** True when only the most recent gifts are listed. */
    giftsTruncated: z.boolean(),
    events: z.array(z.object({
        eventId: z.string().uuid(),
        eventName: z.string(),
        startsAt: z.string().nullable(),
        checkedIn: z.boolean(),
        ticketsBought: z.number().int().nonnegative(),
    })),
    capabilities: z.object({ contact: z.boolean(), message: z.boolean() }),
});
/* ------------------------------------------------------------------- messaging */
export const DONOR_MESSAGE_SUBJECT_MAX = 150;
export const DONOR_MESSAGE_BODY_MAX = 5000;
/** The most donors one message may reach. */
export const DONOR_MESSAGE_RECIPIENT_CAP = 1000;
export const donorMessageAudienceSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("segment"), filters: donorFiltersSchema }).strict(),
    z
        .object({
        kind: z.literal("selected"),
        donorRefs: z.array(donorRefSchema).min(1).max(DONOR_MESSAGE_RECIPIENT_CAP),
    })
        .strict(),
]);
export const donorMessageDraftSchema = z
    .object({
    audience: donorMessageAudienceSchema,
    subject: z.string().trim().min(1).max(DONOR_MESSAGE_SUBJECT_MAX),
    body: z.string().trim().min(1).max(DONOR_MESSAGE_BODY_MAX),
})
    .strict();
/**
 * Sending repeats the draft and the recipient count its preview showed. If the audience has moved
 * since (a new gift, an unsubscribe), the server refuses rather than write to people nobody saw.
 */
export const donorMessageSendSchema = donorMessageDraftSchema
    .extend({ expectedRecipientCount: z.number().int().min(1).max(DONOR_MESSAGE_RECIPIENT_CAP) })
    .strict();
export const donorMessagePreviewResponseSchema = z.object({
    subject: z.string(),
    /** The email as each recipient will read it, footer and unsubscribe line included. */
    body: z.string(),
    recipientCount: z.number().int().nonnegative(),
    excluded: z.object({
        noEmail: z.number().int().nonnegative(),
        unsubscribed: z.number().int().nonnegative(),
        undeliverable: z.number().int().nonnegative(),
    }),
    /** Null when sending is possible; otherwise the sentence that says why not. */
    unavailable: z.string().nullable(),
    /** Messages this organization may still send in the current 24 hours. */
    remainingToday: z.number().int().nonnegative(),
});
export const donorMessageSendResponseSchema = z.object({
    messageId: z.string().uuid(),
    sent: z.number().int().nonnegative(),
    notSent: z.number().int().nonnegative(),
    message: z.string(),
});
/* ---------------------------------------------- the donor's "anonymous to organizations" */
/** The words a request must carry to hide, and to un-hide, so a stray call can flip neither. */
export const HIDE_FROM_ORGANIZATIONS_CONFIRMATION = "hide-from-all-organizations";
export const SHOW_TO_ORGANIZATIONS_CONFIRMATION = "show-to-organizations";
export const setOrganizationVisibilitySchema = z.discriminatedUnion("hiddenFromOrganizations", [
    z
        .object({
        hiddenFromOrganizations: z.literal(true),
        confirm: z.literal(HIDE_FROM_ORGANIZATIONS_CONFIRMATION),
    })
        .strict(),
    z
        .object({
        hiddenFromOrganizations: z.literal(false),
        confirm: z.literal(SHOW_TO_ORGANIZATIONS_CONFIRMATION),
    })
        .strict(),
]);
export const organizationVisibilityResponseSchema = z.object({
    hiddenFromOrganizations: z.boolean(),
    changedAt: z.string().nullable(),
});
/**
 * The donor-facing words for both kinds of anonymity, in one place for donor-web and iOS (GH-858)
 * to use verbatim — `apps/anonymity-copy.test.ts` holds the Swift twin
 * (`AnonymityCopy.swift`) to these exact strings. Long lines sit behind the surface's ⓘ or on the
 * explanation screen (five-word rule); the short labels, the `…Short` lines and every button are
 * what sits on screen, five words or fewer.
 */
export const ANONYMITY_COPY = {
    publicLabel: "Anonymous",
    /** The visible half of the ⓘ beside the give box's anonymity choice. */
    publicShort: "Hidden from the public",
    publicExplanationTitle: "Who sees your name",
    publicExplanation: (organizationName) => `Hide my name from other donors and public displays. ${organizationName} will still see your gift so they can thank you.`,
    /** The event room's "Don't show my name onscreen": the host still sees who sent it. */
    promptExplanation: "Your name stays off the event screen and away from other guests. The host still sees who sent it.",
    /** The donor's own history: anonymous to the public, not to the organization. */
    givenAnonymously: "Anonymous to the public",
    /** The row that leads to the one deep setting, one level below the profile's first screen. */
    advancedPrivacyLabel: "Advanced privacy",
    organizationsLabel: "Anonymous to all organizations",
    organizationsStateOn: "On — organizations see “Anonymous donor”",
    organizationsStateOff: "Off — organizations see your name",
    organizationsExplanationTitle: "What this setting does",
    organizationsExplanation: "Organizations you give to will see “Anonymous donor” instead of your name and contact details — on every gift, past and future. Your gifts still count toward their totals, and your receipts still come from GiveHere. They will not be able to thank you, invite you, or send you updates.",
    /** The explanation screen, one consequence per line, before anything can change. */
    organizationsPoints: [
        "Every organization you’ve given to, and any you give to later, sees “Anonymous donor” instead of your name and contact details.",
        "They can’t thank you, invite you, or send you updates.",
        "Your receipts don’t change. They still come from GiveHere.",
        "Your gifts still count toward their totals.",
        "To turn it off, come back here. Organizations then see your name on your gifts again.",
    ],
    /** Buttons. Five words or fewer. */
    organizationsTurnOn: "Turn on",
    organizationsContinue: "Continue",
    organizationsCancel: "Keep my name visible",
    organizationsTurnOff: "Turn off",
    organizationsConfirmHide: "Hide me from all organizations",
    organizationsConfirmHideTitle: "Hide your name from every organization?",
    organizationsConfirmHideBody: "Every organization you’ve given to will see “Anonymous donor” on all your gifts, past and future, and won’t be able to thank you.",
    organizationsConfirmShow: "Let organizations see my gifts again",
    organizationsConfirmShowBody: "Organizations you’ve given to will see your name and contact details on your gifts again.",
    organizationsConfirmShowButton: "Show my name again",
    organizationsKeepOn: "Keep it on",
};
//# sourceMappingURL=donors.js.map